import { Injectable } from '@angular/core';
import { APIService } from '../../../api/api.service';

export interface FetchResult<T = any> {
  success: boolean;
  data: T | null;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class MrrService {
  constructor(private apiService: APIService) {}

  // ─── Shared Helpers ──────────────────────────────────────────────────────────

  private get authHeader() {
    return { Authorization: localStorage.getItem('t') || '' };
  }

  private async callApi(
    data: any,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const result = await this.apiService.postDataPromis(
        APIService.API_DISCOUNTENPOINT,
        data,
        this.authHeader,
      );
      return {
        success: result?.status === '1' || result?.s === '1',
        message: result?.msg || 'No response message',
      };
    } catch {
      return { success: false, message: 'Something went wrong' };
    }
  }

  private async fetchTab(
    url: string,
    flatten?: (r: any) => any[],
  ): Promise<FetchResult> {
    try {
      const result = await this.apiService.getDataPromis(
        url,
        {},
        this.authHeader,
      );
      if (result?.status === '1') {
        return {
          success: true,
          data: flatten ? flatten(result) : (result.response ?? []),
          message: '',
        };
      }
      return {
        success: false,
        data: [],
        message: result?.error || 'Failed to fetch data',
      };
    } catch {
      return {
        success: false,
        data: [],
        message: 'Unexpected error fetching data',
      };
    }
  }

  private async postTab(
    data: any,
    flatten: (r: any) => any[],
  ): Promise<FetchResult> {
    try {
      const result = await this.apiService.postDataPromis(
        `${APIService.API_DISCOUNTENPOINT}/billing/mrr`,
        data,
        this.authHeader,
      );
      if (result?.s === '1') {
        return { success: true, data: flatten(result), message: '' };
      }
      return {
        success: false,
        data: [],
        message: result?.error || 'Failed to fetch data',
      };
    } catch {
      return {
        success: false,
        data: [],
        message: 'Unexpected error fetching data',
      };
    }
  }

  // ─── MRR ─────────────────────────────────────────────────────────────────────

  async addMrrForChild(child: string, mrrList: any[]) {
    return this.callApi({
      lid: child,
      action: 'add',
      tableType: 'mrr',
      mrrList,
    });
  }

  async updateMrr(formData: any) {
    const item = formData.mrrList?.[0];
    return this.callApi({
      lid: formData.accountId,
      action: 'update',
      tableType: 'mrr',
      serviceName: item?.serviceName,
      mrrDiscount: item?.mrrDiscount,
      startDate: item?.startDate,
      endDate: item?.endDate,
      comment: item?.comment,
      uuid: formData.uuid,
    });
  }

  // ─── S3 ──────────────────────────────────────────────────────────────────────

  async addS3(formData: any) {
    return this.callApi({
      action: 'add',
      tableType: 's3',
      childIds: formData.childIds,
      data: formData.data,
    });
  }

  async addMultipleS3(formData: any) {
    console.log('formData', formData);
    return this.callApi({
      action: 'addMultiple',
      tableType: 's3',
      childIds: formData.childIds,
      regionList: formData.regionList,
      usageTypelist: formData.usageTypelist,
      startDate: formData.startDate,
      endDate: formData.endDate,
      discountType: formData.discountType,
      discount: formData.discount,
      ppu: formData.ppu ?? '',
    });
  }

  async updateS3(formData: any) {
    return this.callApi({
      action: 'update',
      tableType: 's3',
      data: formData.data,
    });
  }

  async addCfrc(formData: any) {
    return this.callApi({ ...formData, action: 'add', tableType: 'cfrc' });
  }

  async addMultipleCfrc(formData: any) {
    return this.callApi({
      action: 'addMultiple',
      tableType: 'cfrc',
      childIds: formData.childIds,
      regionList: formData.regionList,
      usageTypelist: formData.usageTypelist,
      startDate: formData.startDate,
      endDate: formData.endDate,
      discountType: formData.discountType,
      discount: formData.discount,
      ppu: formData.ppu ?? '',
    });
  }

  async updateCfrc(formData: any) {
    return this.callApi({ ...formData, action: 'update', tableType: 'cfrc' });
  }

  deleteCfrc(accountId: string, uuid: string) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=delete&tableType=cfrc&uuid=${uuid}`,
    );
  }

  // ─── RI ──────────────────────────────────────────────────────────────────────

  async addRi(formData: any) {
    return this.callApi({ ...formData, action: 'add', tableType: 'ri' });
  }

  async updateRi(formData: any) {
    return this.callApi({
      accountId: formData.accountId,
      tableType: 'ri',
      action: 'update',
      ...formData.riDetailsList[0],
    });
  }

  deleteRi(accountId: string, id: string) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=delete&tableType=ri&id=${id}`,
    );
  }

  // ─── RDS-RI ──────────────────────────────────────────────────────────────────

  async addRdsRi(formData: any) {
    return this.callApi({ ...formData, action: 'add', tableType: 'rds-ri' });
  }

  async updateRdsRi(formData: any) {
    return this.callApi({ ...formData, action: 'update', tableType: 'rds-ri' });
  }

  // ─── OTP ─────────────────────────────────────────────────────────────────────

  async requestOtp(email: string) {
    try {
      const result = await this.apiService.postDataPromis(
        `${APIService.API_DISCOUNTENPOINT}/request-otp`,
        { email },
        this.authHeader,
      );
      return {
        success: result?.status === '1',
        message: result?.msg || result?.error || 'No response message',
      };
    } catch {
      return { success: false, message: 'Failed to send OTP' };
    }
  }

  async verifyOtp(email: string, otp: string) {
    try {
      const result = await this.apiService.postDataPromis(
        `${APIService.API_DISCOUNTENPOINT}/verify-otp`,
        { email, otp },
        this.authHeader,
      );
      return {
        success: result?.status === '1',
        message: result?.response || result?.error || 'No response message',
      };
    } catch {
      return { success: false, message: 'Failed to verify OTP' };
    }
  }

  // ─── Tab Fetchers ─────────────────────────────────────────────────────────────

  fetchMrrTab({ accountId }: any) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?lid=${accountId}&action=get&tableType=mrr`,
    );
  }

  fetchEc2Tab({ accountId }: any) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=get&tableType=ri`,
    );
  }

  fetchS3Tab({ accountId }: any) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=fetch&tableType=s3`,
      this._flattenS3Data,
    );
  }

  fetchCfrcTab({ accountId }: any) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=get&tableType=cfrc`,
    );
  }

  fetchRdsRiTab(accountId: string) {
    return this.postTab(
      {
        a: 'fetchNonNhaMrr',
        lid: accountId,
        serviceName: 'Amazon Relational Database Service',
      },
      this._flattenRdsData,
    );
  }

  // ─── Flatten Helpers ──────────────────────────────────────────────────────────

  private _flattenEc2Data(raw: any, selectedClientName: string): any[] {
    if (!raw) return [];
    if (selectedClientName === 'NHA') {
      return [
        {
          category: 'Instance',
          billingType: '-',
          isMrr: '-',
          perc: raw.instance ?? 0,
        },
        { category: 'EBS', billingType: '-', isMrr: '-', perc: raw.ebs ?? 0 },
        {
          category: 'Others',
          billingType: '-',
          isMrr: '-',
          perc: raw.others ?? 0,
        },
      ];
    }
    const rows: any[] = [];
    const push = (cat: string, type: string, src: any) =>
      src &&
      rows.push({
        category: cat,
        billingType: type,
        isMrr: src.isMrr,
        perc: src.perc,
      });
    push('Instance', 'RI/SP', raw.instance?.ri_sp);
    push('Instance', 'OD', raw.instance?.od);
    push('Instance', 'Spot', raw.instance?.spot);
    push('EBS', '-', raw.ebs);
    push('Others', '-', raw.others);
    return rows;
  }

  private _flattenS3Data(raw: any): any[] {
    return (raw?.response ?? []).map((item: any) => ({
      region: item.regionn || '-',
      usageType: item.usageType || '-',
      unit: item.unit || '-',
      ppu: item.ppu || '-',
      discount:
        item.discountPercentage != null ? `${item.discountPercentage}%` : '-',
      discountType: item.discountType || '-',
      description: item.description || '-',
      accountId: item.accountId || '-',
      uuid: item.uuid || '-',
      startDate: item.startDate || '-',
      endDate: item.endDate || '-',
    }));
  }

  private _flattenCfrcData(raw: any): any[] {
    return raw?.cfrcMrr
      ? [
          {
            region: '-',
            usageType: 'CloudFront',
            isMrr: raw.cfrcMrr.isMrr,
            perc: raw.cfrcMrr.perc,
          },
        ]
      : [];
  }

  private _flattenRdsData(raw: any): any[] {
    if (!raw) return [];
    const rows: any[] = [];
    const push = (cat: string, type: string, src: any) =>
      src &&
      rows.push({
        category: cat,
        billingType: type,
        isMrr: src.isMrr,
        perc: src.perc,
        dbEngine: '-',
      });
    push('Instance', 'RI/SP', raw.instance?.ri_sp);
    push('Instance', 'OD', raw.instance?.od);
    push('Others', '-', raw.others);
    return rows;
  }

  // after deleteRi(...)

  deleteMrr(lid: string, uuid: string) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?tableType=mrr&lid=${lid}&action=delete&uuid=${uuid}`,
    );
  }

  deleteS3(accountId: string, uuid: string) {
    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?accountId=${accountId}&action=delete&tableType=s3&uuid=${uuid}`,
    );
  }
  deleteAll(accountId: string, tableType: string) {
    const idParam =
      tableType === 'mrr' ? `lid=${accountId}` : `accountId=${accountId}`;

    return this.fetchTab(
      `${APIService.API_DISCOUNTENPOINT}?${idParam}&tableType=${tableType}&action=delete_all`,
    );
  }
}
